import React, { useEffect, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useNavigate } from 'react-router-dom';
import { MapPin, CreditCard, Award, Plus, Tag } from 'lucide-react';
import useCartStore from '../store/cartStore';
import useAuthStore from '../store/authStore';
import { getAddresses, addAddress } from '../api/userAPI';
import { getWallet } from '../api/loyaltyAPI';
import { createOrder, initializePayment } from '../api/orderAPI';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './CheckoutPage.css';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const { cartItems, getCartTotal } = useCartStore();
  useAuthStore();

  const [addresses, setAddresses] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [loading, setLoading] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [newAddress, setNewAddress] = useState({
    fullName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    country: 'Ghana',
    isDefault: false,
  });

  useEffect(() => {
    if (cartItems.length === 0) {
      navigate('/cart');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartItems.length, navigate]);

  const fetchData = async () => {
    try {
      const [addressRes, walletRes] = await Promise.all([
        getAddresses(),
        getWallet(),
      ]);
      if (addressRes.success) {
        setAddresses(addressRes.data);
        const defaultAddr = addressRes.data.find((a) => a.default);
        if (defaultAddr) setSelectedAddress(defaultAddr.id);
        else if (addressRes.data.length > 0)
          setSelectedAddress(addressRes.data[0].id);
      }
      if (walletRes.success) setWallet(walletRes.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    try {
      const response = await addAddress(newAddress);
      if (response.success) {
        toast.success('Address added');
        setShowAddAddress(false);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to add address');
    }
  };

  const subtotal = getCartTotal();
  const pointsValue = pointsToRedeem / 100;
  const estimatedTotal = Math.max(0, subtotal - pointsValue - couponDiscount);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const response = await axiosInstance.get(
        `/coupons/validate?code=${couponCode}&orderAmount=${subtotal}`
      );
      if (response.data.success) {
        const couponData = response.data.data;
        setCoupon(couponData);
        if (couponData.discountType === 'PERCENTAGE') {
          setCouponDiscount(subtotal * (couponData.discountValue / 100));
        } else {
          setCouponDiscount(Math.min(couponData.discountValue, subtotal));
        }
        toast.success('Coupon applied successfully!');
      } else {
        toast.error(response.data.message);
        setCoupon(null);
        setCouponDiscount(0);
      }
    } catch (error) {
      toast.error('Invalid coupon code');
      setCoupon(null);
      setCouponDiscount(0);
    } finally {
      setCouponLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    if (!selectedAddress) {
      toast.error('Please select a delivery address');
      return;
    }

    const selectedAddr = addresses.find((a) => a.id === selectedAddress);
    if (!selectedAddr) {
      toast.error('Invalid address selected');
      return;
    }

    setPlacing(true);
    try {
      const orderData = {
        items: cartItems.map((item) => ({
          productId: item.product.id,
          quantity: item.quantity,
          variationId: item.variation ? item.variation.id : null,
        })),
        deliveryAddress: `${selectedAddr.addressLine1}, ${selectedAddr.city}, ${selectedAddr.region}`,
        pointsToRedeem: pointsToRedeem,
        addressId: selectedAddress,
        couponCode: coupon ? coupon.code : null,
      };

      const orderRes = await createOrder(orderData);
      if (!orderRes.success) {
        toast.error(orderRes.message);
        setPlacing(false);
        return;
      }

      const order = orderRes.data;

      const paymentRes = await initializePayment({
        orderId: order.id,
        callbackUrl: `https://auroragh.com/order-success?orderId=${order.id}`,
      });

      if (!paymentRes.success) {
        toast.error(paymentRes.message);
        setPlacing(false);
        return;
      }

      window.location.href = paymentRes.data.authorization_url;

    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to place order');
      setPlacing(false);
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="checkout-page">
          <div className="container">
            <div className="checkout-loading">Loading checkout...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="checkout-page">
        <div className="container">
          <h1 className="checkout-title">Checkout</h1>

          <div className="checkout-layout">
            {/* Left — Delivery, Points & Coupon */}
            <div className="checkout-left">

              {/* Delivery Address */}
              <div className="checkout-section">
                <div className="checkout-section-header">
                  <MapPin size={20} color="#1565C0" />
                  <h3>Delivery Address</h3>
                </div>

                {addresses.length > 0 ? (
                  <div className="address-list">
                    {addresses.map((address) => (
                      <label key={address.id} className="address-option">
                        <input
                          type="radio"
                          name="address"
                          value={address.id}
                          checked={selectedAddress === address.id}
                          onChange={() => setSelectedAddress(address.id)}
                        />
                        <div className="address-card">
                          <div className="address-card-header">
                            <strong>{address.fullName}</strong>
                            {address.default && (
                              <span className="default-badge">Default</span>
                            )}
                          </div>
                          <p>{address.addressLine1}</p>
                          {address.addressLine2 && <p>{address.addressLine2}</p>}
                          <p>{address.city}, {address.region}</p>
                          <p>{address.phone}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="no-address-text">
                    No saved addresses. Add one below.
                  </p>
                )}

                <button
                  className="add-address-toggle"
                  onClick={() => setShowAddAddress(!showAddAddress)}
                >
                  <Plus size={16} />
                  Add New Address
                </button>

                {showAddAddress && (
                  <form onSubmit={handleAddAddress} className="add-address-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Full Name</label>
                        <input
                          type="text"
                          className="input-field"
                          value={newAddress.fullName}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, fullName: e.target.value })
                          }
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Phone</label>
                        <input
                          type="tel"
                          className="input-field"
                          value={newAddress.phone}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, phone: e.target.value })
                          }
                          required
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Address Line 1</label>
                      <input
                        type="text"
                        className="input-field"
                        value={newAddress.addressLine1}
                        onChange={(e) =>
                          setNewAddress({ ...newAddress, addressLine1: e.target.value })
                        }
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Address Line 2 (Optional)</label>
                      <input
                        type="text"
                        className="input-field"
                        value={newAddress.addressLine2}
                        onChange={(e) =>
                          setNewAddress({ ...newAddress, addressLine2: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>City</label>
                        <input
                          type="text"
                          className="input-field"
                          value={newAddress.city}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, city: e.target.value })
                          }
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Region</label>
                        <input
                          type="text"
                          className="input-field"
                          value={newAddress.region}
                          onChange={(e) =>
                            setNewAddress({ ...newAddress, region: e.target.value })
                          }
                          required
                        />
                      </div>
                    </div>
                    <label className="default-checkbox">
                      <input
                        type="checkbox"
                        checked={newAddress.isDefault}
                        onChange={(e) =>
                          setNewAddress({ ...newAddress, isDefault: e.target.checked })
                        }
                      />
                      Set as default address
                    </label>
                    <button type="submit" className="save-address-btn">
                      Save Address
                    </button>
                  </form>
                )}
              </div>

              {/* Points Redemption */}
              {wallet && wallet.auroraPoints > 0 && (
                <div className="checkout-section">
                  <div className="checkout-section-header">
                    <Award size={20} color="#1565C0" />
                    <h3>Redeem Aurora Points</h3>
                  </div>
                  <div className="points-redemption">
                    <div className="points-balance">
                      <span>Available Points</span>
                      <strong>{wallet.auroraPoints} pts</strong>
                    </div>
                    <div className="points-balance">
                      <span>Points Value</span>
                      <strong>GHS {(wallet.auroraPoints / 100).toFixed(2)}</strong>
                    </div>
                    <div className="points-input-group">
                      <label>Points to Redeem</label>
                      <input
                        type="number"
                        className="input-field"
                        min={0}
                        max={Math.min(wallet.auroraPoints, subtotal * 100)}
                        value={pointsToRedeem}
                        onChange={(e) => setPointsToRedeem(Number(e.target.value))}
                        placeholder="Enter points to redeem"
                      />
                      <p className="points-hint">100 points = GHS 1.00 discount</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Coupon Code */}
              <div className="checkout-section">
                <div className="checkout-section-header">
                  <Tag size={20} color="#1565C0" />
                  <h3>Coupon Code</h3>
                </div>
                <div className="coupon-input">
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={!!coupon}
                  />
                  {coupon ? (
                    <button
                      className="remove-coupon-btn"
                      onClick={() => {
                        setCoupon(null);
                        setCouponCode('');
                        setCouponDiscount(0);
                      }}
                    >
                      Remove
                    </button>
                  ) : (
                    <button
                      className="apply-coupon-btn"
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                    >
                      {couponLoading ? 'Checking...' : 'Apply'}
                    </button>
                  )}
                </div>
                {coupon && (
                  <div className="coupon-applied-row">
                    <span>Coupon ({coupon.code})</span>
                    <span>- GHS {couponDiscount.toFixed(2)}</span>
                  </div>
                )}
              </div>

            </div>

            {/* Right — Order Summary */}
            <div className="checkout-right">
              <div className="checkout-summary">
                <h3 className="summary-title">Order Summary</h3>

                <div className="checkout-items">
                  {cartItems.map((item) => (
                    <div key={item.id} className="checkout-item">
                      <div className="checkout-item-info">
                        <span className="checkout-item-name">{item.product?.name}</span>
                        <span className="checkout-item-qty">x{item.quantity}</span>
                      </div>
                      <span className="checkout-item-price">
                        GHS {(item.unitPrice * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="checkout-summary-rows">
                  <div className="summary-row">
                    <span>Subtotal</span>
                    <span>GHS {subtotal.toFixed(2)}</span>
                  </div>
                  {pointsToRedeem > 0 && (
                    <div className="summary-row discount-row">
                      <span>Points Discount</span>
                      <span>- GHS {pointsValue.toFixed(2)}</span>
                    </div>
                  )}
                  {coupon && (
                    <div className="summary-row discount-row">
                      <span>Coupon ({coupon.code})</span>
                      <span>- GHS {couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span>Loyalty Card Discount</span>
                    <span className="applied-text">Applied at processing</span>
                  </div>
                </div>

                <div className="checkout-total">
                  <span>Total</span>
                  <span>GHS {estimatedTotal.toFixed(2)}</span>
                </div>

                <div className="payment-method">
                  <CreditCard size={16} color="#1565C0" />
                  <span>Secure payment via Paystack</span>
                </div>

                <button
                  className="place-order-btn"
                  onClick={handlePlaceOrder}
                  disabled={placing}
                >
                  {placing ? 'Processing...' : `Pay GHS ${estimatedTotal.toFixed(2)}`}
                </button>

                <p className="checkout-note">
                  By placing this order, you agree to our Terms of Service and Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CheckoutPage;